import { NodeV2Style } from '@vedalib/editor'
import * as R from 'ramda'
import React from 'react'

import { safeParsePxSizes } from '../../formControls/Sizes/utils'
import SvgText from './SvgText'
import { COLOR_BORDER, COLOR_MARGIN, COLOR_PADDING } from './constants'

const getRectPath = (rect: { x: number; y: number; width: number; height: number }) => {
  if (rect.width <= 0 || rect.height <= 0) {
    return ''
  }
  return `M${rect.x},${rect.y} h${rect.width} v${rect.height} h${-rect.width} z`
}

const getLayerPath = (
  outerRect: { x: number; y: number; width: number; height: number },
  innerRect: { x: number; y: number; width: number; height: number },
) => {
  const outerPath = getRectPath(outerRect)
  const innerPath = getRectPath(innerRect)
  return !R.equals(outerPath, innerPath) && outerPath && innerPath && `${outerPath} ${innerPath}`
}

interface ISvgBoxModelProps {
  size: { width: number; height: number }
  styles: NodeV2Style | null
}

const SvgBoxModel: React.FC<ISvgBoxModelProps> = ({ size, styles }) => {
  const { width, height } = size

  const margin = safeParsePxSizes(styles?.margin)
  const marginTop = margin[0] || 0
  const marginRight = margin[1] || 0
  const marginBottom = margin[2] || 0
  const marginLeft = margin[3] || 0

  const border = safeParsePxSizes(styles?.borderWidth)
  const borderTop = border[0] || 0
  const borderRight = border[1] || 0
  const borderBottom = border[2] || 0
  const borderLeft = border[3] || 0

  const padding = safeParsePxSizes(styles?.padding)
  const paddingTop = padding[0] || 0
  const paddingRight = padding[1] || 0
  const paddingBottom = padding[2] || 0
  const paddingLeft = padding[3] || 0

  const marginAreaRect = {
    x: 0,
    y: 0,
    width: width,
    height: height,
  }

  const borderAreaRect = {
    x: marginLeft,
    y: marginTop,
    width: Math.max(0, width - (marginLeft + marginRight)),
    height: Math.max(0, height - (marginTop + marginBottom)),
  }

  const paddingAreaRect = {
    x: borderAreaRect.x + borderLeft,
    y: borderAreaRect.y + borderTop,
    width: Math.max(0, borderAreaRect.width - (borderLeft + borderRight)),
    height: Math.max(0, borderAreaRect.height - (borderTop + borderBottom)),
  }

  const contentRect = {
    x: paddingAreaRect.x + paddingLeft,
    y: paddingAreaRect.y + paddingTop,
    width: Math.max(0, paddingAreaRect.width - (paddingLeft + paddingRight)),
    height: Math.max(0, paddingAreaRect.height - (paddingTop + paddingBottom)),
  }

  const marginPathData = getLayerPath(marginAreaRect, borderAreaRect)
  const borderPathData = getLayerPath(borderAreaRect, paddingAreaRect)
  const paddingPathData = getLayerPath(paddingAreaRect, contentRect)

  return (
    <>
      {marginPathData && (
        <path
          d={marginPathData}
          fill={COLOR_MARGIN}
          fillRule='evenodd'
          stroke={COLOR_MARGIN}
          strokeWidth='0.5'
        />
      )}
      {marginTop > 0 && (
        <SvgText
          border={COLOR_MARGIN}
          text={marginTop}
          x={marginAreaRect.width / 2}
          y={marginTop / 2}
        />
      )}
      {marginBottom > 0 && (
        <SvgText
          border={COLOR_MARGIN}
          text={marginBottom}
          x={marginAreaRect.width / 2}
          y={marginAreaRect.height - marginBottom / 2}
        />
      )}
      {marginLeft > 0 && (
        <SvgText
          border={COLOR_MARGIN}
          text={marginLeft}
          transform={`rotate(-90 ${marginLeft / 2}, ${marginAreaRect.height / 2})`}
          x={marginLeft / 2}
          y={marginAreaRect.height / 2}
        />
      )}
      {marginRight > 0 && (
        <SvgText
          border={COLOR_MARGIN}
          text={marginRight}
          transform={`rotate(90 ${marginAreaRect.width - marginRight / 2}, ${marginAreaRect.height / 2})`}
          x={marginAreaRect.width - marginRight / 2}
          y={marginAreaRect.height / 2}
        />
      )}

      {borderPathData && (
        <path
          d={borderPathData}
          fill={COLOR_BORDER}
          fillRule='evenodd'
          stroke={COLOR_BORDER}
          strokeWidth='0.5'
        />
      )}
      {borderTop > 0 && (
        <SvgText
          border={COLOR_BORDER}
          text={borderTop}
          x={borderAreaRect.x + borderAreaRect.width / 2}
          y={borderAreaRect.y + borderTop / 2}
        />
      )}
      {borderBottom > 0 && (
        <SvgText
          border={COLOR_BORDER}
          text={borderBottom}
          x={borderAreaRect.x + borderAreaRect.width / 2}
          y={borderAreaRect.y + borderAreaRect.height - borderBottom / 2}
        />
      )}
      {borderLeft > 0 && (
        <SvgText
          border={COLOR_BORDER}
          text={borderLeft}
          transform={`rotate(-90 ${borderAreaRect.x + borderLeft / 2}, ${borderAreaRect.y + borderAreaRect.height / 2})`}
          x={borderAreaRect.x + borderLeft / 2}
          y={borderAreaRect.y + borderAreaRect.height / 2}
        />
      )}
      {borderRight > 0 && (
        <SvgText
          border={COLOR_BORDER}
          text={borderRight}
          transform={`rotate(90 ${borderAreaRect.x + borderAreaRect.width - borderRight / 2}, ${borderAreaRect.y + borderAreaRect.height / 2})`}
          x={borderAreaRect.x + borderAreaRect.width - borderRight / 2}
          y={borderAreaRect.y + borderAreaRect.height / 2}
        />
      )}

      {paddingPathData && (
        <path
          d={paddingPathData}
          fill={COLOR_PADDING}
          fillRule='evenodd'
          stroke={COLOR_PADDING}
          strokeWidth='0.5'
        />
      )}
      {paddingTop > 0 && (
        <SvgText
          border={COLOR_PADDING}
          text={paddingTop}
          x={paddingAreaRect.x + paddingAreaRect.width / 2}
          y={paddingAreaRect.y + paddingTop / 2}
        />
      )}
      {paddingBottom > 0 && (
        <SvgText
          border={COLOR_PADDING}
          text={paddingBottom}
          x={paddingAreaRect.x + paddingAreaRect.width / 2}
          y={paddingAreaRect.y + paddingAreaRect.height - paddingBottom / 2}
        />
      )}
      {paddingLeft > 0 && (
        <SvgText
          border={COLOR_PADDING}
          text={paddingLeft}
          transform={`rotate(-90 ${paddingAreaRect.x + paddingLeft / 2}, ${paddingAreaRect.y + paddingAreaRect.height / 2})`}
          x={paddingAreaRect.x + paddingLeft / 2}
          y={paddingAreaRect.y + paddingAreaRect.height / 2}
        />
      )}
      {paddingRight > 0 && (
        <SvgText
          border={COLOR_PADDING}
          text={paddingRight}
          transform={`rotate(90 ${paddingAreaRect.x + paddingAreaRect.width - paddingRight / 2}, ${paddingAreaRect.y + paddingAreaRect.height / 2})`}
          x={paddingAreaRect.x + paddingAreaRect.width - paddingRight / 2}
          y={paddingAreaRect.y + paddingAreaRect.height / 2}
        />
      )}
    </>
  )
}

export default React.memo(SvgBoxModel)
